import {
    Grid,
    List,
    ListItem,
    ListItemText,
    Theme,
    withStyles,
    WithStyles,
    Typography,
    Collapse,
    IconButton,
    CircularProgress
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { FC, Fragment, useState, useEffect } from 'react'
import { getFirstCharCapitalized } from '../../../libs/strings'
import { MuiClasses } from '../../../types'
import { formatStringIfDate } from '../../../utils/dates'
import PaymentPopup from '../../../containers/PayForPublish/PaymentPopup'
import { Apartment } from '../../../types/apartments'
import { Application } from '../../../types/applications'
import ApplicationsApi from '../../../services/applications'

const styles = ({ breakpoints }: Theme) => ({
    listItems: {
        padding: '0',
        margin: '0',
        marginBottom: '13px',
        marginTop: '3px',
        color: '#0000008A',
    },
    listItemsTopPart: {
        padding: '0',
        margin: '0',
        color: '#0000008A',
    },
    listItemsTitle: {
        padding: '0',
        paddingTop: '7px',
        margin: '0',
        'text-transform': 'uppercase',
        fontSize: '0.7rem !important',
        '& p': {
            color: '#000000DE',
            fontFamily: 'CircularBold'
        },
    },
    spouseLifeSituation: {
        '& span': {
            fontSize: '1.2rem'
        }
    },
    applicantInfoSubheader: {
        marginTop: '16px',
        marginBottom: '16px',
    },
})

export interface CustomListWithCreditInfoProps extends WithStyles {
    classes: MuiClasses
    TitleTypography?: JSX.Element
    data: any
    applicants: any
    informationOrder: string[]
    translationPath: string
    t: i18next.TFunction
    multiSelectPaths?: { [key: string]: string }
    hideListItemLogic?: (key: string, value: string) => boolean
    capitalizeFirstLetter?: boolean,
    apartment?: Apartment,
    application?: Application
}

interface ListItemProps {
    t: TFunction
    objectValue: string
    capitalizeFirstLetter?: boolean
}

interface MultiSelectTranslationProps extends ListItemProps {
    multiSelectPath: string
}

const getTranslatedMultiValue = ({
    t,
    multiSelectPath,
    objectValue,
    capitalizeFirstLetter
}: MultiSelectTranslationProps) => {
    const translatedValue = t(`${multiSelectPath}.${objectValue}`)
    return capitalizeFirstLetter
        ? getFirstCharCapitalized(translatedValue)
        : translatedValue
}

const getCapitalized = ({
    t,
    objectValue,
    capitalizeFirstLetter
}: ListItemProps) => {
    const translatedValue = t(`${formatStringIfDate(objectValue)}`)
    return capitalizeFirstLetter
        ? getFirstCharCapitalized(translatedValue)
        : translatedValue
}

const CustomListWithCreditInfo: FC<CustomListWithCreditInfoProps> = ({
    classes,
    t,
    TitleTypography,
    data,
    applicants,
    informationOrder,
    translationPath,
    multiSelectPaths,
    hideListItemLogic,
    capitalizeFirstLetter,
    apartment,
    application
}) => {

    const [showCreditInfoSection, setShowCreditInfoSection] = useState(false)
    const [showBudgetInfoSection, setShowBudgetInfoSection] = useState(false)
    const [budgetInfo, setBudgetInfo] = useState(null)
    const [loadingBudgetInfo, setLoadingBudgetInfo] = useState(false)

    useEffect(() => {
        if (showBudgetInfoSection && application?.rights?.some(right => right.includes("9"))) {
            setLoadingBudgetInfo(true)
            fetchBudgetInfo()
        }
    }, [showBudgetInfoSection, application])

    const handleToggleCreditInfo = () => {
        const hasCreditRights = application?.rights?.some(right => right.includes("7"))
        const hasBudgetRights = application?.rights?.some(right => right.includes("9"))

        if (!hasCreditRights && !hasBudgetRights) {
            // Both unpaid: toggle and close the other
            setShowCreditInfoSection(prev => {
                if (!prev) setShowBudgetInfoSection(false)
                return !prev
            })
        } else {
            setShowCreditInfoSection(prev => !prev)
        }
    }

    const handleToggleBudgetInfo = () => {
        const hasCreditRights = application?.rights?.some(right => right.includes("7"))
        const hasBudgetRights = application?.rights?.some(right => right.includes("9"))

        if (!hasCreditRights && !hasBudgetRights) {
            // Both unpaid: toggle and close the other
            setShowBudgetInfoSection(prev => {
                if (!prev) setShowCreditInfoSection(false)
                return !prev
            })
        } else {
            setShowBudgetInfoSection(prev => !prev)
        }
    }

    const fetchBudgetInfo = async () => {
        try {
            const response = await ApplicationsApi.getFinancialInfo(application.code) // Replace with your actual endpoint
            const data = await response
            setBudgetInfo(data.cases)
        } catch (err) {
            console.error("Error fetching budget info", err)
        } finally {
            setLoadingBudgetInfo(false)
        }
    }

    const renderSwitch = ({
        key,
        objectValue,
        multiSelectPath,
        isErrorOrNoAnswer
    }: any) => {
        switch (key) {
            case 'phoneNumber':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <a
                            style={{
                                textDecoration: 'none',
                                fontFamily: 'CircularRegular'
                            }}
                            href={`tel:+${objectValue}`}
                        >
                            +{objectValue}
                        </a>
                    </Grid>
                )
            case 'pets':
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        <ListItemText
                            primary={getCapitalized({
                                t,
                                objectValue,
                                capitalizeFirstLetter
                            })}
                            className={classes.listItems}
                        />
                    </Grid>
                )
            default:
                return (
                    <Grid item xs={12} md={6} lg={6}>
                        {multiSelectPath && !isErrorOrNoAnswer ? (
                            <>
                                <ListItemText
                                    primary={getTranslatedMultiValue({
                                        t,
                                        objectValue,
                                        capitalizeFirstLetter,
                                        multiSelectPath
                                    })}
                                    className={classes.listItems}
                                />
                            </>
                        ) : (
                            <>
                                <ListItemText
                                    primary={getCapitalized({
                                        t,
                                        objectValue,
                                        capitalizeFirstLetter
                                    })}
                                    className={classes.listItems}
                                />
                            </>
                        )}
                    </Grid>
                )
        }
    }
    return (
        <Fragment>
            {/* POISTA TÄMÄ AIKANAAN */}
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            {/* <pre>{JSON.stringify(applicants, null, 2)}</pre> */}
            {TitleTypography}

            <Typography
                variant='h5'
                className={classes.applicantInfoSubheader}
            >{t('applicantCard.generalInformation')}</Typography>

            <List
                component={'div' as any}
                disablePadding
                className={classes.innerList}
            >
                {informationOrder.map((key: string, index: number) => {
                    const isErrorOrNoAnswer = hideListItemLogic
                        ? data[key] === 'error' || hideListItemLogic(key, data[key])
                        : data[key] === 'error'
                    let objectValue = isErrorOrNoAnswer ? undefined : data[key]
                    const multiSelectPath = multiSelectPaths && multiSelectPaths[key]
                    let newPath = ''
                    if (
                        (key === 'livelihood' || key === 'spouseLivelihood') &&
                        Array.isArray(data[key])
                    ) {
                        newPath = `newApplication.livelihood`
                        objectValue = data[key]
                            .map((value: string) =>
                                value === 'myAnswer'
                                    ? data['livelihoodCustom']
                                    : t(`newApplication.livelihoodOptions.${value}`)
                            )
                            .join(', ')
                    }
                    if (key === 'movingAbout') {
                        newPath = 'application.list.readyForMoveDate'
                    }
                    if (
                        key === 'lifeSituationCustom' ||
                        key === 'spouseLifeSituationCustom' ||
                        key === 'spouseLifeSituation'
                    ) {
                        newPath = `application.list.lifeSituation`
                    }

                    if (key === 'depositPaymentMethodCustom') {
                        newPath = 'application.list.depositPaymentMethod'
                    }

                    if (objectValue === 'nextMonth') {
                        return <></>
                    }

                    if (key === 'spouseCreditStatus') {
                        newPath = 'application.list.creditStatusByApplicant'
                    }
                    if (key === 'creditStatus') {
                        newPath = 'application.list.creditStatusByApplicant'
                    }
                    return (
                        objectValue && (
                            <>
                                {key === 'spouseLifeSituation' && (
                                    <ListItemText
                                        primary={t('application.suposeTitle')}
                                        className={classes.spouseLifeSituation}
                                        style={{
                                            margin: '10px 0'
                                        }}
                                    />
                                )}
                                <ListItem key={`${key}+${index}`} className={classes.listItemsTopPart}>
                                    <Grid
                                        container
                                        item
                                        direction='row'
                                        justify='space-between'

                                    >
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                secondary={
                                                    newPath !== ''
                                                        ? t(`${newPath}`)
                                                        : t(`${translationPath}${key}`)
                                                }
                                                className={classes.listItemsTitle}
                                            />
                                        </Grid>
                                        {renderSwitch({
                                            key,
                                            objectValue,
                                            multiSelectPath,
                                            isErrorOrNoAnswer
                                        })}
                                    </Grid>
                                </ListItem>
                            </>
                        )
                    )
                })}
            </List>
            {applicants && applicants.length > 0 && (
                <>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        margin: '12px 0',
                        // border: '1px solid #C3C6D1',
                        borderRadius: '4px',
                        // padding: '12px 16px',
                        // marginBottom: '18px',
                        backgroundColor: '#E7ECEF' // optional subtle background
                    }} onClick={handleToggleCreditInfo}>
                        <Typography variant="h6" style={{ flexGrow: 1, marginLeft: '8px' }}>
                            {t('applicantCard.showCreditInfoSection')} {/* or your custom title */}
                        </Typography>
                        <IconButton>
                            {showCreditInfoSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </div>

                    <Collapse in={showCreditInfoSection} timeout="auto" unmountOnExit>
                        {!application?.rights?.some(right => right.includes("7")) ? (
                            <>
                                {/* <hr style={{ border: '1px solid #C3C6D1', borderTop: 'none', marginTop: '11px', marginBottom: '24px' }} /> */}
                                <PaymentPopup
                                    paymentProviderWidth={
                                        window.innerWidth < 380 ? '80px' :
                                            window.innerWidth < 520 ? '100px' :
                                                '150px'
                                    }
                                    productTag="showCreditInfo"
                                    headertext={t('paymentPopup.headerTexts.showCreditInfo')}
                                    infotext={t('paymentPopup.suggestTexts.showCreditInfo')}
                                    apartment={apartment}
                                    application={application}
                                />
                            </>
                        ) : (
                            <List component={'div' as any} disablePadding className={classes.innerList}>
                                {applicants.map((applicant, applicantIndex) => (
                                    <>
                                        <div key={applicantIndex}>
                                            {applicants.length > 1 && (
                                                <hr
                                                    style={{
                                                        border: '1px solid #C3C6D1',
                                                        borderTop: 'none',
                                                        marginTop: '11px',
                                                        marginBottom: '24px'
                                                    }}
                                                />
                                            )}
                                            <Typography variant='h5' className={classes.applicantInfoSubheader}>
                                                {applicants.length > 1
                                                    ? `${t('applicantCard.applicant')} ${applicantIndex + 1} ${t('applicantCard.personalInformation')}`
                                                    : `${t('applicantCard.personalInformation')}`}
                                            </Typography>
                                            <ListItem className={classes.listItems}>
                                                <Grid container item direction='row' justify='space-between' style={{ margin: '1px 0' }}>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText secondary={t('applicantCard.name')} className={classes.listItemsTitle} />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText
                                                            primary={applicant.name}
                                                            className={classes.listItems}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText secondary={t('applicantCard.idNumber')} className={classes.listItemsTitle} />
                                                    </Grid>
                                                    <Grid item xs={12} md={6} lg={6}>
                                                        <ListItemText
                                                            primary={applicant.idNumber}
                                                            className={classes.listItems}
                                                        />
                                                    </Grid>
                                                    {applicant.phone &&
                                                        <>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.phone')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={`+${applicant.phone.replace(/^(\+?)/, '')}`}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                        </>
                                                    }
                                                    {applicant.email &&
                                                        <>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.email')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={applicant.email}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                        </>
                                                    }
                                                    {/* <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText secondary={t('applicantCard.status')} className={classes.listItemsTitle} />
                                        </Grid>
                                        <Grid item xs={12} md={6} lg={6}>
                                            <ListItemText
                                                primary={applicant.status}
                                                className={classes.listItems}
                                            />
                                        </Grid> */}
                                                    {applicant.creditInfo && (
                                                        <>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.creditStatus')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={t(`applicantCard.${applicant.creditStatus}`)}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText secondary={t('applicantCard.paymentDefaultCount')} className={classes.listItemsTitle} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6} lg={6}>
                                                                <ListItemText
                                                                    primary={applicant.creditInfo?.paymentDefaultCount}
                                                                    className={classes.listItems}
                                                                />
                                                            </Grid>
                                                        </>
                                                    )}
                                                    {applicant.creditInfo && applicant.creditInfo.paymentDefaultCount > 0 && applicant.creditInfo.creditInformationEntryRow && (
                                                        <>
                                                            {(Array.isArray(applicant.creditInfo.creditInformationEntryRow)
                                                                ? applicant.creditInfo.creditInformationEntryRow
                                                                : [applicant.creditInfo.creditInformationEntryRow]).map((entry: any, index: any) => (
                                                                    <>
                                                                        <Grid item xs={12}>
                                                                            <Typography
                                                                                variant='h6'
                                                                                style={{ marginTop: '15px', fontFamily: 'CircularBold' }}
                                                                            >
                                                                                {applicant.creditInfo.creditInformationEntryRow.length > 1
                                                                                    ? `${t('applicantCard.creditDefaultInformation')} ${index + 1}`
                                                                                    : t('applicantCard.creditDefaultInformation')}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid container key={index} style={{ margin: '5px 0' }}>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditor')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditor} className={classes.listItems} />
                                                                            </Grid>
                                                                            {entry.amountOfPaymentDefault && entry.amountOfPaymentDefault > 0 && (
                                                                                <>
                                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                                        <ListItemText secondary={t('applicantCard.amountOfPaymentDefault')} className={classes.listItemsTitle} />
                                                                                    </Grid>
                                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                                        <ListItemText primary={entry.amountOfPaymentDefault} className={classes.listItems} />
                                                                                    </Grid>
                                                                                </>
                                                                            )}
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditInformationEntryText')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditInformationEntryText} className={classes.listItems} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditInformationEntryDate')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditInformationEntryDate} className={classes.listItems} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.creditInformationEntryExpires')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.creditInformationEntryExpires} className={classes.listItems} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText secondary={t('applicantCard.sourceofinformation')} className={classes.listItemsTitle} />
                                                                            </Grid>
                                                                            <Grid item xs={12} md={6} lg={6}>
                                                                                <ListItemText primary={entry.sourceOfInformation} className={classes.listItems} />
                                                                            </Grid>
                                                                        </Grid>
                                                                    </>
                                                                ))}
                                                        </>
                                                    )}
                                                </Grid>
                                            </ListItem>
                                        </div >
                                    </>
                                ))}
                            </List>
                        )}
                    </Collapse>
                </>
            )}
            {application?.budgetInformation?.some(entry =>
                entry?.status === 'Finished'
            ) && (
                    <>
                        <hr style={{ border: '1px solid #C3C6D1', borderTop: 'none', marginTop: '-12px', marginBottom: '-12px'}} />

                        <div style={{ marginBottom: '26px', }} >
                            <div style={{
                                display: 'flex', alignItems: 'center', cursor: 'pointer', margin: '12px 0', borderRadius: '4px',
                                // padding: '12px 16px',
                                // marginBottom: '18px',
                                backgroundColor: '#E7ECEF'
                            }} onClick={handleToggleBudgetInfo}>
                                <Typography variant="h6" style={{ flexGrow: 1, marginLeft: '8px' }}>
                                    {t('applicantCard.showBudgetInfoSection')} {/* or your custom title */}
                                </Typography>
                                <IconButton>
                                    {showBudgetInfoSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </div>
                            <Collapse in={showBudgetInfoSection} timeout="auto" unmountOnExit>
                                {application?.rights?.some(right => right.includes("9")) ? (
                                    loadingBudgetInfo ? (
                                        <div style={{ padding: '16px', textAlign: 'center' }}>
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <>
                                            {budgetInfo && (Array.isArray(budgetInfo) ? budgetInfo : [budgetInfo]).map((entry, index) => (
                                                <Fragment key={index}>
                                                    {Array.isArray(budgetInfo) && budgetInfo.length > 1 && (
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant='h6'
                                                                style={{ marginTop: '15px', fontFamily: 'CircularBold' }}
                                                            >
                                                                {`${t('applicantCard.budgetInfoSection.applicantTitle')} ${index + 1}`}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    <Grid container style={{ margin: '12px 0' }}>
                                                        {entry?.data?.consumerName && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.applicantName')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.consumerName}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}

                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant='h6'
                                                                className={classes.listItems}
                                                                style={{ marginTop: '12px', marginBottom: '6px', fontFamily: 'CircularBold' }}

                                                            >
                                                                {t('applicantCard.budgetInfoSection.incomeSubheader')}
                                                            </Typography>
                                                        </Grid>
                                                        {entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.income?.primary > 0 && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.primaryIncome')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.income.primary}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.income?.secondary > 0 && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.secondaryIncome')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.income.secondary}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.income?.grossAndBonus > 0 && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.grossAndBonus')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.income.grossAndBonus}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.income?.supplement > 0 && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.supplementaryIncome')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.income.supplement}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.income?.total > 0 && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.totalIncome')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.income.total}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}


                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant='h6'
                                                                className={classes.listItems}
                                                                style={{ marginTop: '12px', marginBottom: '6px', fontFamily: 'CircularBold' }}

                                                            >
                                                                {t('applicantCard.budgetInfoSection.expensesSubheader')}
                                                            </Typography>
                                                        </Grid>
                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.expenses?.housing?.total === 'number' && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.housingExpenses')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.expenses.housing.total}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.expenses?.transport?.total === 'number' && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.transportExpenses')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.expenses.transport.total}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}

                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.expenses?.additional?.total === 'number' && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.additionalExpenses')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.expenses.additional.total}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}

                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.expenses?.debtAndLoanRepayments?.total === 'number' && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.debtExpenses')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.expenses.debtAndLoanRepayments.total}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}

                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.expenses?.total === 'number' && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.totalExpenses')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.expenses.total}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}

                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant='h6'
                                                                className={classes.listItems}
                                                                style={{ marginTop: '12px', marginBottom: '6px', fontFamily: 'CircularBold' }}

                                                            >
                                                                {t('applicantCard.budgetInfoSection.summarySubheader')}
                                                            </Typography>
                                                        </Grid>
                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.last90Days?.budget?.disposableAmount === 'number' && (
                                                            <>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        secondary={t('applicantCard.budgetInfoSection.disposableAmount')}
                                                                        className={classes.listItemsTitle}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} md={6} lg={6}>
                                                                    <ListItemText
                                                                        primary={entry.data.budgetOutput.psd2.insights.last90Days.budget.disposableAmount}
                                                                        className={classes.listItems}
                                                                    />
                                                                </Grid>
                                                            </>
                                                        )}
                                                        {typeof entry?.data?.budgetOutput?.psd2?.insights?.currentBalance === 'number' &&
                                                            entry?.data?.updatedOn && (
                                                                <>
                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                        <ListItemText
                                                                            secondary={`${t('applicantCard.budgetInfoSection.balancePer')} ${new Date(entry.data.updatedOn).toLocaleDateString('fi-FI')}`}
                                                                            className={classes.listItemsTitle}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={6} lg={6}>
                                                                        <ListItemText
                                                                            primary={entry.data.budgetOutput.psd2.insights.currentBalance}
                                                                            className={classes.listItems}
                                                                        />
                                                                    </Grid>
                                                                </>
                                                            )}
                                                    </Grid>
                                                </Fragment>
                                            ))}
                                        </>
                                    )
                                ) : (
                                    <PaymentPopup
                                        paymentProviderWidth={
                                            window.innerWidth < 380 ? '80px' :
                                                window.innerWidth < 520 ? '100px' : '150px'
                                        }
                                        productTag="showBudgetInfo"
                                        headertext={t('paymentPopup.headerTexts.showBudgetInfo')}
                                        infotext={t('paymentPopup.suggestTexts.showBudgetInfo')}
                                        apartment={apartment}
                                        application={application}
                                    />
                                )}
                            </Collapse>

                        </div>
                    </>
                )}
        </Fragment >
    )
}

export default withStyles(styles)(CustomListWithCreditInfo)
